import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Clock, CreditCard, Globe, Trash2, type LucideIcon } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@clarix/ui';
import type { PatchWorkspaceSettingsRequest, MasterRow } from '@clarix/shared-types';
import { SettingsField, SettingsSaveBar, SettingsSection } from '../SettingsSection';
import { useOrganizationSettingsApi } from './api-context';
import { FormSkeleton } from './FormSkeleton';
import { GeoCombobox } from './GeoCombobox';

/**
 * Organization settings tab — Company Profile, Regional, Billing, Work Settings.
 * Every dropdown is sourced from a MasterSystem category via useMasters().
 *
 * UX: shows the saved server state on load; the user edits a single dirty draft;
 * "Save changes" PATCHes only the diff against the server snapshot.
 */

const MONTHS = [
  { code: 1, name: 'January' },
  { code: 2, name: 'February' },
  { code: 3, name: 'March' },
  { code: 4, name: 'April' },
  { code: 5, name: 'May' },
  { code: 6, name: 'June' },
  { code: 7, name: 'July' },
  { code: 8, name: 'August' },
  { code: 9, name: 'September' },
  { code: 10, name: 'October' },
  { code: 11, name: 'November' },
  { code: 12, name: 'December' },
];

type SectionId = 'company' | 'regional' | 'billing' | 'work';

/**
 * Field layout: the standalone admin page uses Zoho-style "row" fields
 * (label + hint on the left, control on the right, divided rows). The setup
 * wizard keeps the compact "stacked" layout that fits its narrow card.
 */
const FieldLayoutContext = createContext<'row' | 'stacked'>('stacked');

/** Left-nav sections for the standalone settings page (not the setup wizard). */
const SECTIONS: { id: SectionId; label: string; hint: string; icon: LucideIcon }[] = [
  { id: 'company', label: 'Company Profile', hint: 'Identity & branding', icon: Building2 },
  { id: 'regional', label: 'Regional Settings', hint: 'Locale & calendar', icon: Globe },
  { id: 'billing', label: 'Billing Defaults', hint: 'Currency & rates', icon: CreditCard },
  { id: 'work', label: 'Work Settings', hint: 'Hours & capacity', icon: Clock },
];

function MasterSelect({
  id,
  rows,
  loading,
  value,
  onChange,
  placeholder,
  formatLabel,
}: {
  id: string;
  rows: MasterRow[] | undefined;
  loading: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  /** Custom option/value label (e.g. currency symbol + name). Defaults to name. */
  formatLabel?: (row: MasterRow) => React.ReactNode;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={loading || !rows?.length}>
      <SelectTrigger id={id} className="h-9">
        <SelectValue placeholder={loading ? 'Loading…' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {(rows ?? []).map((row) => (
          <SelectItem key={row.id} value={row.code}>
            {formatLabel ? formatLabel(row) : row.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface OrganizationSettingsHandle {
  /** Commits any pending edits. Resolves after PATCH/upload/delete succeed. */
  save: () => Promise<void>;
  /** Whether the form currently has uncommitted edits. */
  isDirty: () => boolean;
}

/** A section the host app owns: it gets the same header, jump and scroll as the built-in ones. */
export interface OrganizationExtraSection {
  /** The `?section=` value that jumps to it. */
  id: string;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  /** Unsaved edits: they light up the page's Save bar like the built-in fields. */
  dirty?: boolean;
  /** Runs on the page's Save, before the built-in fields; throw to stop the save. */
  save?: () => Promise<void>;
  /** Runs on the page's Discard. */
  discard?: () => void;
  children: React.ReactNode;
}

export interface OrganizationSettingsProps {
  slug: string;
  /** Standalone page only: sections after Work Settings that the host app renders. */
  extraSections?: OrganizationExtraSection[];
  /**
   * When true, the inner sticky "Save changes / Discard" bar is hidden. The
   * setup wizard uses this so its own footer can drive a unified "Save & next"
   * button via the imperative `save()` handle below.
   */
  hideSaveBar?: boolean;
  /** Notified whenever the form's dirty state flips. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * When set, render only these sections (used by the setup wizard to show one
   * block at a time; a block may hold more than one section). Omitted on the
   * admin settings page → all sections. Also switches sections to a "bare"
   * style (no nested card chrome) so they sit cleanly inside the wizard card.
   */
  visibleSections?: Array<'company' | 'regional' | 'billing' | 'work'>;
  /**
   * Reports which required fields within the visible sections are still empty
   * (e.g. ['timezone','locale']). The wizard uses this to gate "Continue".
   * Pass a stable callback (e.g. a useState setter).
   */
  onRequiredMissingChange?: (missing: string[]) => void;
}

export const OrganizationSettings = forwardRef<
  OrganizationSettingsHandle,
  OrganizationSettingsProps
>(function OrganizationSettings(
  { slug, hideSaveBar, onDirtyChange, visibleSections, onRequiredMissingChange, extraSections },
  ref,
) {
  const {
    useSettings,
    usePatch,
    useUploadLogo,
    useDeleteLogo,
    useMasters,
    useGeoCountries,
    useGeoStates,
    useGeoCities,
    normalizeError,
  } = useOrganizationSettingsApi();

  const settings = useSettings(slug, { skip: !slug });
  const [patch, patchState] = usePatch();
  const [uploadLogo, uploadLogoState] = useUploadLogo();
  const [deleteLogo, deleteLogoState] = useDeleteLogo();

  // Registered-address geography (public /payroll/locations catalog). States
  // and cities cascade off the draft-or-saved selection; cities are
  // server-searched (a state can hold thousands).
  const [citySearch, setCitySearch] = useState('');
  // Names picked in THIS session, so the combobox can label a fresh selection
  // before the PATCH round-trips the resolved *_name fields.
  const [pickedGeoNames, setPickedGeoNames] = useState<{
    country?: string | null;
    state?: string | null;
    city?: string | null;
  }>({});

  // Master catalogs feeding every dropdown on this form.
  const industries = useMasters('industry');
  const orgTypes = useMasters('org_type');
  const teamSizes = useMasters('team_size_range');
  const timezones = useMasters('timezone');
  const locales = useMasters('locale');
  const currencies = useMasters('currency');
  const dateFormats = useMasters('date_format');
  const weekStartDays = useMasters('week_start_day');

  // Local edit draft. Hydrated when the GET completes; user edits in-place.
  const [draft, setDraft] = useState<PatchWorkspaceSettingsRequest>({});

  // Wizard mode (sections filtered) → bare layout, and the statutory/address
  // group is not rendered at all. Declared here so the geo queries below can
  // skip: nothing consumes their results in the wizard.
  const bare = !!visibleSections;

  // Effective (draft-over-saved) geo selection driving the cascading pickers.
  const effCountryId =
    (draft.country_id !== undefined ? draft.country_id : settings.data?.country_id) ?? null;
  const effStateId =
    (draft.state_id !== undefined ? draft.state_id : settings.data?.state_id) ?? null;
  const geoCountries = useGeoCountries({ skip: bare });
  const geoStates = useGeoStates(bare ? null : effCountryId);
  const geoCities = useGeoCities(bare ? null : effStateId, citySearch);

  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current && settings.data) {
      hydrated.current = true;
    }
  }, [settings.data]);

  // Logo edits are deferred until Save — we preview locally and only POST/DELETE
  // when the user clicks "Save changes".
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoRemove, setPendingLogoRemove] = useState(false);
  /**
   * Inline logo-specific error message. Populated by:
   *   • client-side pre-check on file pick (size > 2 MB, wrong MIME)
   *   • the backend upload response when the user clicks Save (e.g. server's
   *     own "file exceeds 2 MB limit" 413, scan failure, etc.)
   * Cleared whenever the user picks a new file or removes the pending one.
   */
  const [logoError, setLogoError] = useState<string | null>(null);
  // Standalone-page section selection (ignored in wizard/`bare` mode). Driven
  // by ?section= so the shell's sidebar panel ("General Settings") can switch
  // sections; the in-page nav below lg is the mobile fallback.
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection: string =
    SECTIONS.some((s) => s.id === sectionParam) || extraSections?.some((s) => s.id === sectionParam)
      ? (sectionParam as string)
      : 'company';
  const pendingLogoPreview = useMemo(
    () => (pendingLogoFile ? URL.createObjectURL(pendingLogoFile) : null),
    [pendingLogoFile],
  );
  useEffect(() => {
    return () => {
      if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview);
    };
  }, [pendingLogoPreview]);

  // The inline logo banner shows the client-side validation error (set on file
  // pick) if present, otherwise the upload mutation's error. Derived during
  // render so there's no setState-in-effect cascade. We don't surface
  // `deleteLogo` here — delete failures still hit the catch-all `error` block.
  const displayedLogoError =
    logoError ?? (uploadLogoState.error ? normalizeError(uploadLogoState.error).message : null);

  // Selected currency row — kept above early returns so hook order is stable.
  const currencyMeta = useMemo(() => {
    const code = draft.currency ?? settings.data?.currency;
    return currencies.data?.rows.find((r) => r.code === code);
  }, [currencies.data, settings.data?.currency, draft.currency]);

  // All hooks must run on every render — keep them above the early returns,
  // otherwise React throws "Rendered more hooks than during the previous render"
  // when settings.isLoading flips from true to false.
  const builtinDirty = Object.keys(draft).length > 0 || pendingLogoFile !== null || pendingLogoRemove;
  const extraDirty = !!extraSections?.some((s) => s.dirty);
  const hasDirty = builtinDirty || extraDirty;

  useEffect(() => {
    onDirtyChange?.(hasDirty);
  }, [hasDirty, onDirtyChange]);

  // Report which required fields in the visible sections are still empty, so
  // the setup wizard can disable "Continue" until they're filled.
  useEffect(() => {
    if (!onRequiredMissingChange) return;
    const ws = settings.data as Record<string, unknown> | undefined;
    const draftRec = draft as Record<string, unknown>;
    const REQUIRED: Record<string, string[]> = {
      company: ['name'],
      regional: ['timezone', 'locale'],
      billing: ['currency'],
      work: [],
    };
    const visible = visibleSections ?? ['company', 'regional', 'billing', 'work'];
    const valueOf = (k: string) => (k in draftRec ? draftRec[k] : ws?.[k]);
    const missing: string[] = [];
    for (const s of visible) {
      for (const k of REQUIRED[s] ?? []) {
        const v = valueOf(k);
        if (v === undefined || v === null || String(v).trim() === '') missing.push(k);
      }
    }
    onRequiredMissingChange(missing);
  }, [draft, settings.data, visibleSections, onRequiredMissingChange]);

  const handleSave = useCallback(async () => {
    if (!hasDirty) return;
    // Host sections first: their checks fail most often, and a failure leaves the rest unsaved.
    for (const s of extraSections ?? []) if (s.dirty) await s.save?.();
    if (pendingLogoFile) {
      await uploadLogo({ slug, file: pendingLogoFile }).unwrap();
    } else if (pendingLogoRemove && settings.data?.logo_url) {
      // Guard: skip the DELETE if the server has nothing to delete (avoids a
      // no-op call when the user staged then discarded a local upload).
      await deleteLogo({ slug }).unwrap();
    }
    if (Object.keys(draft).length > 0) {
      await patch({ slug, body: draft }).unwrap();
    }
    setPendingLogoFile(null);
    setPendingLogoRemove(false);
    setDraft({});
    // Server response now carries the resolved *_name fields — session picks done.
    setPickedGeoNames({});
  }, [
    hasDirty,
    pendingLogoFile,
    pendingLogoRemove,
    settings.data?.logo_url,
    draft,
    slug,
    uploadLogo,
    deleteLogo,
    patch,
    extraSections,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      isDirty: () => hasDirty,
    }),
    [handleSave, hasDirty],
  );

  const handleSaveClick = useCallback(async () => {
    try {
      await handleSave();
    } catch {
      /* error rendered below via patch/upload/delete query state */
    }
  }, [handleSave]);

  // ── Standalone-page section navigation ──
  // The admin settings page renders ALL sections as one long form (a normal,
  // scrollable settings page). The in-page nav + the shell's sidebar panel
  // both jump to a section via ?section=…; a scroll-spy keeps the nav in sync
  // as the user scrolls. None of this runs in the wizard (bare) mode.
  // Jump to the section selected from the shell's "General" sidebar panel
  // (?section=…). Sections are located by their `data-section` attribute.
  useEffect(() => {
    if (visibleSections) return;
    const el = document.querySelector<HTMLElement>(`[data-section="${activeSection}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeSection, visibleSections]);

  if (!slug) return null;
  if (settings.isLoading || !settings.data) {
    return <FormSkeleton rows={7} />;
  }
  if (settings.isError) {
    return (
      <div className="bg-destructive/10 text-destructive rounded-md p-3 text-xl">
        {normalizeError(settings.error).message}
        <Button className="ml-3" type="button" onClick={() => settings.refetch?.()}>
          Retry
        </Button>
      </div>
    );
  }

  const ws = settings.data;
  const get = <K extends keyof PatchWorkspaceSettingsRequest>(
    field: K,
  ): PatchWorkspaceSettingsRequest[K] => {
    if (field in draft) return draft[field];
    return (ws as unknown as PatchWorkspaceSettingsRequest)[field];
  };
  const set = (input: Partial<PatchWorkspaceSettingsRequest>) =>
    setDraft((prev) => ({ ...prev, ...input }));

  const handleDiscard = () => {
    extraSections?.forEach((s) => s.discard?.());
    setDraft({});
    setPendingLogoFile(null);
    setPendingLogoRemove(false);
    setPickedGeoNames({});
    setCitySearch('');
  };

  const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB — matches backend limit.
  const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);

  const handleLogoSelect = (file: File) => {
    // Pre-checks so the user gets immediate feedback without waiting for Save
    // to round-trip the upload and bounce off the backend's 2 MB / MIME guard.
    // Clear any prior upload-mutation error so a fresh pick starts clean (the
    // banner derives from uploadLogoState.error too).
    uploadLogoState.reset?.();
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(
        `file exceeds 2 MB limit (${(file.size / (1024 * 1024)).toFixed(2)} MB selected)`,
      );
      setPendingLogoFile(null);
      return;
    }
    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      setLogoError('Unsupported file type. Use PNG, JPEG, WEBP, or SVG.');
      setPendingLogoFile(null);
      return;
    }
    setLogoError(null);
    setPendingLogoFile(file);
    setPendingLogoRemove(false);
  };

  const handleLogoRemove = () => {
    setLogoError(null);
    uploadLogoState.reset?.();
    setPendingLogoFile(null);
    // Only schedule a server-side DELETE if the server actually has a logo.
    // Otherwise this was just discarding a locally-staged upload — no API call
    // needed on save.
    setPendingLogoRemove(ws.logo_url != null);
  };

  // What's actually shown in the logo slot, accounting for pending edits.
  const displayedLogoUrl = pendingLogoFile
    ? pendingLogoPreview
    : pendingLogoRemove
      ? null
      : ws.logo_url;

  const currencySymbol = (currencyMeta?.meta_json?.symbol as string | undefined) ?? '$';

  // uploadLogo errors are surfaced inline beneath the logo slot via `logoError`,
  // so they're intentionally excluded from the form-level banner below.
  const error = patchState.error
    ? normalizeError(patchState.error).message
    : deleteLogoState.error
      ? normalizeError(deleteLogoState.error).message
      : null;

  const logoSlot = (
    <SettingsField
      label="Company logo"
      id="logo"
      hint="PNG / JPEG / WEBP / SVG · ≤ 2 MB. Shown in the switcher and on documents."
    >
      <div>
        <div className="relative h-20 w-20 shrink-0">
        <label
          htmlFor="logo"
          aria-label={displayedLogoUrl ? 'Replace logo' : 'Add logo'}
          style={
            displayedLogoUrl
              ? {
                  backgroundImage: `url(${displayedLogoUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
          className="border-border bg-surface hover:border-primary/60 block h-20 w-20 cursor-pointer rounded-md border border-dashed"
        >
          {!displayedLogoUrl && (
            <div className="text-muted-foreground hover:text-primary flex h-full w-full flex-col items-center justify-center text-sm">
              <span className="text-2xl leading-none">+</span>
              <span>Add logo</span>
            </div>
          )}
          <input
            id="logo"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleLogoSelect(f);
              e.target.value = '';
            }}
          />
        </label>
          {displayedLogoUrl && (
            <button
              type="button"
              onClick={handleLogoRemove}
              aria-label="Remove logo"
              className="bg-surface text-destructive ring-destructive/30 hover:text-destructive absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full shadow ring-1"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
        {displayedLogoError && (
          <p role="alert" className="text-destructive mt-1.5 max-w-[16rem] text-sm leading-tight">
            {displayedLogoError}
          </p>
        )}
      </div>
    </SettingsField>
  );

  const show = (s: 'company' | 'regional' | 'billing' | 'work') =>
    !visibleSections || visibleSections.includes(s);
  // `bare` (declared above the geo queries) also drops the nested-card chrome so
  // fields sit cleanly inside the SetupCard instead of looking like a full-width
  // panel.
  // Only show a per-section header in wizard mode when a block holds more than
  // one section (Billing + Work). A single-section block relies on the
  // SetupCard's own title/subtitle, so its section header is suppressed to
  // avoid a duplicate subtitle.
  const bareShowHeader = bare && (visibleSections?.length ?? 0) > 1;

  // Which section renders. Wizard (bare) shows whatever sections the block
  // holds; the standalone page renders ALL sections as one long form so it
  // reads like a normal settings page.
  const visible = (s: SectionId) => (bare ? show(s) : true);

  // Whether a required field is currently empty (drives the inline indicator).
  const isReqEmpty = (k: 'name' | 'timezone' | 'locale' | 'currency') => {
    const v = get(k);
    return v === undefined || v === null || String(v).trim() === '';
  };

  return (
    <FieldLayoutContext.Provider value={bare ? 'stacked' : 'row'}>
    {/* Standalone page: a full-height column — the form body scrolls and the
        save bar is its in-flow footer, so the bar spans the page content only
        and never sits under the shell's settings sidebar panel. The wizard
        (hideSaveBar) keeps the plain flow layout inside its SetupCard. */}
    <div className={cn(hideSaveBar ? 'space-y-4' : 'flex min-h-0 flex-1 flex-col')}>
      {bare && (
        <style>{`
          @keyframes ofFieldRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
          .setup-field { animation: ofFieldRise .4s ease both; }
          .setup-field:nth-child(1){animation-delay:.03s}
          .setup-field:nth-child(2){animation-delay:.07s}
          .setup-field:nth-child(3){animation-delay:.11s}
          .setup-field:nth-child(4){animation-delay:.15s}
          .setup-field:nth-child(5){animation-delay:.19s}
          .setup-field:nth-child(6){animation-delay:.23s}
          .setup-field:nth-child(7){animation-delay:.27s}
          .setup-field:nth-child(8){animation-delay:.31s}
          @media (prefers-reduced-motion: reduce) { .setup-field { animation: none; } }
        `}</style>
      )}
      <div
        className={cn(
          !bare && 'flex flex-col gap-6',
          // The scrolling body: everything above the save-bar footer.
          !hideSaveBar && 'min-h-0 flex-1 overflow-y-auto pb-6',
        )}
      >
        {/* All sections render as one flat canvas form. Section navigation
            lives in the shell's "General" sidebar panel (jumps via ?section=). */}
        <div className="min-w-0 flex-1 space-y-8">
          {/* ───── Company Profile ───── */}
          {visible('company') && (
            <Section
              bare={bare}
              showHeader={bareShowHeader}
              icon={Building2}
              title="Company Profile"
              subtitle="Workspace identity shown to members and on documents."
              dataSection="company"
            >
              {/* Logo is managed in Admin → settings later; hidden during first-run setup. */}
              {!bare && logoSlot}
              <Field
                label="Workspace display name"
                id="name"
                hint="Shown in the switcher and headers."
                required
                error={isReqEmpty('name')}
              >
                <Input
                  id="name"
                  className="h-9"
                  placeholder="e.g. Acme Inc."
                  value={(get('name') as string) ?? ws.name ?? ''}
                  onChange={(e) => set({ name: e.target.value })}
                  maxLength={120}
                />
              </Field>

              <Field
                label="Company name (legal)"
                id="company_name"
                hint="Used on invoices and exports."
              >
                <Input
                  id="company_name"
                  className="h-9"
                  placeholder="e.g. Acme Private Limited"
                  value={
                    ((get('company_name') as string | null | undefined) ?? '') as string
                  }
                  onChange={(e) => set({ company_name: e.target.value || null })}
                  maxLength={160}
                />
              </Field>

              <Field label="Website" id="website">
                <Input
                  id="website"
                  type="url"
                  className="h-9"
                  placeholder="https://example.com"
                  value={(get('website') as string | null | undefined) ?? ''}
                  onChange={(e) => set({ website: e.target.value || null })}
                />
              </Field>

              <Field label="Industry" id="industry">
                <MasterSelect
                  id="industry"
                  rows={industries.data?.rows}
                  loading={industries.isLoading}
                  value={(get('industry') as string) ?? ws.industry ?? ''}
                  onChange={(v) => set({ industry: v })}
                  placeholder="Select industry"
                />
              </Field>

              <Field label="Organization type" id="org_type">
                <MasterSelect
                  id="org_type"
                  rows={orgTypes.data?.rows}
                  loading={orgTypes.isLoading}
                  value={(get('org_type') as string) ?? ws.org_type ?? ''}
                  onChange={(v) => set({ org_type: v })}
                  placeholder="Select organization type"
                />
              </Field>

              <Field label="Team size" id="team_size">
                <MasterSelect
                  id="team_size"
                  rows={teamSizes.data?.rows}
                  loading={teamSizes.isLoading}
                  value={(get('team_size') as string) ?? ws.team_size ?? ''}
                  onChange={(v) => set({ team_size: v })}
                  placeholder="Select team size"
                />
              </Field>

              <Field
                label="Description"
                id="description"
                hint="Shown in the workspace switcher (≤ 280 chars)."
                wide
              >
                <textarea
                  id="description"
                  rows={3}
                  maxLength={280}
                  placeholder="A short description of your workspace"
                  value={(get('description') as string | null | undefined) ?? ''}
                  onChange={(e) => set({ description: e.target.value || null })}
                  className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full resize-none rounded-md border px-3 py-2 text-xl shadow-sm focus-visible:outline-none focus-visible:ring-1"
                />
              </Field>

              {/* ── Statutory identity & registered address ──
                  Org-level fields the payroll app reads through its
                  GET /organization shim (payslips, statutory filings, and the
                  PT/LWF-relevant registered address). Geo ids come from the
                  public /payroll/locations catalog (MasterSystem UUIDs).

                  Managed in Admin → settings, hidden during first-run setup
                  (same as the logo slot above): none of it is needed to stand
                  a workspace up, and eleven optional statutory/address fields
                  bury the six that actually identify the workspace. Payroll
                  collects them when that app is switched on. */}
              {!bare && (
                <>
                  <Field
                    label="GSTIN"
                    id="gst_number"
                    hint="Optional · 15-character India GSTIN. Used on payroll documents."
                  >
                    <Input
                      id="gst_number"
                      className="h-9"
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      autoCapitalize="characters"
                      spellCheck={false}
                      value={(get('gst_number') as string | null | undefined) ?? ''}
                      onChange={(e) => set({ gst_number: e.target.value.toUpperCase() || null })}
                    />
                  </Field>

                  <Field label="Contact email" id="contact_email" hint="Company contact shown on documents.">
                    <Input
                      id="contact_email"
                      type="email"
                      className="h-9"
                      placeholder="finance@example.com"
                      value={
                        (get('contact_email') as string | null | undefined) ?? ''
                      }
                      onChange={(e) => set({ contact_email: e.target.value || null })}
                    />
                  </Field>

                  <Field label="Contact phone" id="contact_phone" hint="Dial code + number.">
                    <div className="flex gap-2">
                      <Input
                        id="contact_phone_country_code"
                        aria-label="Dial code"
                        className="h-9 w-20"
                        placeholder="91"
                        inputMode="numeric"
                        maxLength={8}
                        value={
                          (get('contact_phone_country_code') as string | null | undefined) ?? ''
                        }
                        onChange={(e) =>
                          set({
                            contact_phone_country_code:
                              e.target.value.replace(/\D/g, '') || null,
                          })
                        }
                      />
                      <Input
                        id="contact_phone"
                        className="h-9 flex-1"
                        placeholder="98250 00000"
                        value={
                          (get('contact_phone') as string | null | undefined) ?? ''
                        }
                        onChange={(e) => set({ contact_phone: e.target.value || null })}
                      />
                    </div>
                  </Field>

                  <Field label="Address line 1" id="address_line1" hint="Registered office address.">
                    <Input
                      id="address_line1"
                      className="h-9"
                      value={
                        (get('address_line1') as string | null | undefined) ?? ''
                      }
                      onChange={(e) => set({ address_line1: e.target.value || null })}
                      maxLength={255}
                    />
                  </Field>

                  <Field label="Address line 2" id="address_line2">
                    <Input
                      id="address_line2"
                      className="h-9"
                      value={
                        (get('address_line2') as string | null | undefined) ?? ''
                      }
                      onChange={(e) => set({ address_line2: e.target.value || null })}
                      maxLength={255}
                    />
                  </Field>

                  <Field
                    label="Country"
                    id="country_id"
                    hint="Drives the state & city choices below."
                  >
                    <GeoCombobox
                      id="country_id"
                      options={geoCountries.data ?? []}
                      loading={geoCountries.isLoading}
                      value={effCountryId}
                      valueName={
                        'country_id' in draft ? (pickedGeoNames.country ?? null) : ws.country_name
                      }
                      onChange={(opt) => {
                        set({ country_id: opt?.id ?? null, state_id: null, city_id: null });
                        setPickedGeoNames({ country: opt?.name ?? null, state: null, city: null });
                        setCitySearch('');
                      }}
                      placeholder="Select country"
                    />
                  </Field>

                  <Field label="State" id="state_id" hint="The registered state — payroll resolves PT/LWF from it.">
                    <GeoCombobox
                      id="state_id"
                      options={geoStates.data ?? []}
                      loading={geoStates.isLoading}
                      value={effStateId}
                      valueName={'state_id' in draft ? (pickedGeoNames.state ?? null) : ws.state_name}
                      onChange={(opt) => {
                        set({ state_id: opt?.id ?? null, city_id: null });
                        setPickedGeoNames((p) => ({ ...p, state: opt?.name ?? null, city: null }));
                        setCitySearch('');
                      }}
                      placeholder="Select state"
                      disabled={!effCountryId}
                    />
                  </Field>

                  <Field label="City" id="city_id">
                    <GeoCombobox
                      id="city_id"
                      options={geoCities.data ?? []}
                      loading={geoCities.isLoading}
                      value={(draft.city_id !== undefined ? draft.city_id : ws.city_id) ?? null}
                      valueName={'city_id' in draft ? (pickedGeoNames.city ?? null) : ws.city_name}
                      onChange={(opt) => {
                        set({ city_id: opt?.id ?? null });
                        setPickedGeoNames((p) => ({ ...p, city: opt?.name ?? null }));
                      }}
                      placeholder="Select city"
                      disabled={!effStateId}
                      onSearchChange={setCitySearch}
                    />
                  </Field>

                  <Field label="Pincode" id="pincode">
                    <Input
                      id="pincode"
                      className="h-9"
                      placeholder="380015"
                      maxLength={20}
                      value={(get('pincode') as string | null | undefined) ?? ''}
                      onChange={(e) => set({ pincode: e.target.value || null })}
                    />
                  </Field>
                </>
              )}
            </Section>
          )}

          {/* ───── Regional Settings ───── */}
          {visible('regional') && (
            <Section
              bare={bare}
              showHeader={bareShowHeader}
              icon={Globe}
              title="Regional Settings"
              subtitle="Locale defaults that shape dates, numbers, and calendars across the workspace."
              dataSection="regional"
            >
              <Field label="Default timezone" id="timezone" required error={isReqEmpty('timezone')}>
                <MasterSelect
                  id="timezone"
                  rows={timezones.data?.rows}
                  loading={timezones.isLoading}
                  value={(get('timezone') as string) ?? ws.timezone}
                  onChange={(v) => set({ timezone: v })}
                  placeholder="Select timezone"
                />
              </Field>

              <Field label="Default locale" id="locale" required error={isReqEmpty('locale')}>
                <MasterSelect
                  id="locale"
                  rows={locales.data?.rows}
                  loading={locales.isLoading}
                  value={(get('locale') as string) ?? ws.locale}
                  onChange={(v) => set({ locale: v })}
                  placeholder="Select locale"
                />
              </Field>

              <Field label="Date format" id="date_format">
                <MasterSelect
                  id="date_format"
                  rows={dateFormats.data?.rows}
                  loading={dateFormats.isLoading}
                  value={(get('date_format') as string) ?? ws.date_format ?? ''}
                  onChange={(v) => set({ date_format: v })}
                  placeholder="Select date format"
                />
              </Field>

              <Field label="Week starts on" id="week_start_day">
                <MasterSelect
                  id="week_start_day"
                  rows={weekStartDays.data?.rows}
                  loading={weekStartDays.isLoading}
                  value={(get('week_start_day') as string) ?? ws.week_start_day ?? ''}
                  onChange={(v) => set({ week_start_day: v })}
                  placeholder="Select week start day"
                />
              </Field>

              <Field label="Fiscal year start" id="fiscal_year_start_month">
                <Select
                  value={String(
                    (get('fiscal_year_start_month') as number | null | undefined) ??
                      ws.fiscal_year_start_month ??
                      '',
                  )}
                  onValueChange={(v) => set({ fiscal_year_start_month: Number(v) })}
                >
                  <SelectTrigger id="fiscal_year_start_month" className="h-9">
                    <SelectValue placeholder="Select fiscal start month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.code} value={String(m.code)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Section>
          )}

          {/* ───── Billing Defaults ───── */}
          {visible('billing') && (
            <Section
              bare={bare}
              showHeader={bareShowHeader}
              icon={CreditCard}
              title="Billing Defaults"
              subtitle="Used by invoices, timesheet billing, and project budgeting."
              dataSection="billing"
            >
              <Field label="Default currency" id="currency" required error={isReqEmpty('currency')}>
                <MasterSelect
                  id="currency"
                  rows={currencies.data?.rows}
                  loading={currencies.isLoading}
                  value={(get('currency') as string) ?? ws.currency}
                  onChange={(v) => set({ currency: v })}
                  placeholder="Select currency"
                  formatLabel={(row) => (
                    <span className="flex items-center gap-2">
                      <span className="bg-muted text-foreground inline-flex h-5 w-5 items-center justify-center rounded text-sm font-semibold">
                        {(row.meta_json?.symbol as string | undefined) ?? row.code.slice(0, 1)}
                      </span>
                      <span>{row.name}</span>
                    </span>
                  )}
                />
              </Field>

              <Field label="Default hourly rate" id="default_hourly_rate">
                <div className="relative">
                  <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl font-medium">
                    {currencySymbol}
                  </span>
                  <Input
                    id="default_hourly_rate"
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="0.00"
                    className="h-9 pl-8"
                    value={
                      (get('default_hourly_rate') as number | null | undefined) ??
                      ws.default_hourly_rate ??
                      ''
                    }
                    onChange={(e) =>
                      set({
                        default_hourly_rate: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
              </Field>
            </Section>
          )}

          {/* ───── Work Settings ───── */}
          {visible('work') && (
            <Section
              bare={bare}
              showHeader={bareShowHeader}
              icon={Clock}
              title="Work Settings"
              subtitle="Drives timesheet capacity, leave-day calculations, and project effort estimates."
              dataSection="work"
            >
              <Field label="Working hours per day" id="work_hours_per_day">
                <Input
                  id="work_hours_per_day"
                  type="number"
                  step="0.25"
                  min={0}
                  max={24}
                  className="h-9"
                  placeholder="e.g. 8"
                  value={
                    (get('work_hours_per_day') as number | null | undefined) ??
                    ws.work_hours_per_day ??
                    ''
                  }
                  onChange={(e) =>
                    set({
                      work_hours_per_day: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </Field>

              <Field label="Working hours per week" id="work_hours_per_week">
                <Input
                  id="work_hours_per_week"
                  type="number"
                  step="0.25"
                  min={0}
                  max={24 * 7}
                  className="h-9"
                  placeholder="e.g. 40"
                  value={
                    (get('work_hours_per_week') as number | null | undefined) ??
                    ws.work_hours_per_week ??
                    ''
                  }
                  onChange={(e) =>
                    set({
                      work_hours_per_week: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </Field>
            </Section>
          )}

          {!bare &&
            extraSections?.map((s) => (
              <SettingsSection
                key={s.id}
                className="mx-5"
                dataSection={s.id}
                icon={s.icon}
                title={s.title}
                subtitle={s.subtitle}
              >
                {s.children}
              </SettingsSection>
            ))}

          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-xl">
              {error}
            </div>
          )}
        </div>
      </div>

      {!hideSaveBar && (
        // Always rendered (disabled when clean) so the Save button never shifts.
        <SettingsSaveBar
          onDiscard={handleDiscard}
          onSave={handleSaveClick}
          disabled={!hasDirty}
          saving={patchState.isLoading || uploadLogoState.isLoading || deleteLogoState.isLoading}
        />
      )}
    </div>
    </FieldLayoutContext.Provider>
  );
});

function Section({
  title,
  subtitle,
  icon: Icon,
  children,
  headerExtra,
  bare = false,
  showHeader = true,
  dataSection,
}: {
  title: string;
  subtitle: string;
  /** Header icon (standalone settings cards). Omitted in wizard/bare mode. */
  icon?: LucideIcon;
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
  /** Wizard mode: drop the nested card border/background so fields sit flat. */
  bare?: boolean;
  /** Whether to render the section header. In bare mode this is only true for
   *  blocks holding multiple sections (so single-section blocks don't repeat
   *  the SetupCard's own title/subtitle). */
  showHeader?: boolean;
  /** Standalone page: id so the jump nav / scroll-spy can target it. */
  dataSection?: string;
}) {
  // Wizard (bare) mode — compact stacked grid that fits the narrow SetupCard.
  if (bare) {
    return (
      <section data-section={dataSection} className="mb-1">
        {showHeader && (
          <header className="mb-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-muted-foreground text-sm font-bold uppercase tracking-wide">
                {title}
              </h3>
              {headerExtra}
            </div>
          </header>
        )}
        <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2">{children}</div>
      </section>
    );
  }

  // Standalone admin page — borderless "canvas" layout: a section header and
  // divided label-left / control-right field rows, flat on the page.
  return (
    <SettingsSection
      className="mx-5"
      dataSection={dataSection}
      icon={Icon}
      title={title}
      subtitle={subtitle}
      headerExtra={headerExtra}
    >
      {children}
    </SettingsSection>
  );
}

function Field({
  label,
  id,
  hint,
  required,
  error,
  wide,
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  required?: boolean;
  /** Required field currently empty — shows a red ring + informative message. */
  error?: boolean;
  /** Span both columns of the stacked (wizard) grid. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  const layout = useContext(FieldLayoutContext);

  // Standalone admin page — label + hint on the left, control on the right.
  if (layout === 'row') {
    return (
      <SettingsField label={label} id={id} hint={hint} required={required} error={error}>
        {children}
      </SettingsField>
    );
  }

  // Wizard (bare) mode — compact stacked field.
  return (
    <div className={cn('setup-field grid gap-2', wide && 'sm:col-span-2')}>
      <Label htmlFor={id} className="text-foreground text-lg font-medium">
        {label}
        {required && (
          <span className="text-destructive ml-0.5 font-bold" aria-hidden>
            *
          </span>
        )}
      </Label>
      <div className={error ? 'ring-destructive/30 rounded-lg ring-2' : undefined}>{children}</div>
      {error ? (
        <p role="alert" className="text-destructive flex items-center gap-1 text-lg font-medium">
          <span className="bg-destructive inline-block h-1 w-1 rounded-full" />
          {label} is required to continue
        </p>
      ) : (
        hint && <p className="text-muted-foreground text-lg leading-tight">{hint}</p>
      )}
    </div>
  );
}
